import { formatEuros } from "./format.js";

/**
 * The sell counter's bookkeeping, with no React in it — the one piece of
 * CLIENT-OWNED state on the trader screen, and it has to survive the stash
 * changing underneath it.
 *
 * Staging is a list of iids and nothing else: an `ItemInstance` would be a
 * stash the next profile has replaced wholesale, so `counterView` re-resolves
 * every id against the stash as it is NOW, and an id that stops resolving is
 * simply not in the view (the dialogs' re-resolve-or-die pattern).
 *
 * The batch is sequential: the server accepts `MAX_TRADE_MSGS_PER_SEC` trade
 * messages and DROPS the rest in silence, and a sale is not predicted, so one
 * sale is in flight at a time and the next is sent when the previous is
 * answered. `SellBatch` is that state machine.
 */

/** One line on the counter: what is being sold and what it fetches. */
export interface CounterRow {
  iid: string;
  /** The item's name, with its stack count when it has more than one. */
  label: string;
  /** What THIS trader pays for it, contents and all (`sellPriceDeep`). */
  price: number;
}

/** The counter as it is drawn, and the staging as it should now be. */
export interface CounterView {
  rows: readonly CounterRow[];
  /** What the confirm button pays out. Zero for an empty counter. */
  total: number;
  /**
   * The staged ids that still resolve, in staging order — handed back so the
   * component can write the pruned list into its state (see `sameStaging`).
   */
  iids: readonly string[];
}

/** What the caller has to be able to say about one staged id. */
export type PriceLookup = (iid: string) => { label: string; price: number } | null;

/**
 * The staged ids, priced and pruned. `null` from the lookup means "not on this
 * counter any more", whatever the reason; there is no "still there but he will
 * not buy it" state, since a row nobody pays for is not a row.
 */
export function counterView(staged: readonly string[], priceOf: PriceLookup): CounterView {
  const rows: CounterRow[] = [];
  let total = 0;
  for (const iid of staged) {
    const priced = priceOf(iid);
    if (priced === null) continue;
    rows.push({ iid, label: priced.label, price: priced.price });
    total += priced.price;
  }
  return { rows, total, iids: rows.map((r) => r.iid) };
}

/** Whether two staging lists say the same thing, in the same order. */
export function sameStaging(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * `iid` on the counter, appended. Idempotent — the same item can be staged
 * three ways and must not sell twice — and the SAME array comes back when
 * nothing changed.
 */
export function stage(staged: readonly string[], iid: string): readonly string[] {
  return staged.includes(iid) ? staged : [...staged, iid];
}

/** `iid` off the counter. The same array back when it was not on it. */
export function unstage(staged: readonly string[], iid: string): readonly string[] {
  return staged.includes(iid) ? staged.filter((s) => s !== iid) : staged;
}

/**
 * What the confirm button says: the count AND the money, two questions in one
 * glance. The button stays on screen with an empty counter, shut and saying
 * what it waits for — a control that is not there cannot be discovered.
 */
export function sellButtonLabel(view: CounterView): string {
  const n = view.rows.length;
  if (n === 0) return "Nothing on the counter";
  return `Sell ${n} item${n === 1 ? "" : "s"} · ${formatEuros(view.total)}`;
}

// ---------------------------------------------------------------------------
// The batch
// ---------------------------------------------------------------------------

/**
 * A confirmed counter, going over one sale at a time. `queue` is frozen at
 * confirm time: re-deriving it from the shrinking counter would re-decide a
 * decision the player made. An id gone unsellable gets a refusal, which changes
 * nothing.
 */
export interface SellBatch {
  readonly queue: readonly string[];
  /** How many have been ANSWERED. The progress number the button prints. */
  readonly done: number;
  /** The sale waiting for its answer, or `null` between the two. */
  readonly inFlight: string | null;
}

/** A batch over `iids`, or `null` for an empty counter (nothing to run). */
export function startSellBatch(iids: readonly string[]): SellBatch | null {
  return iids.length === 0 ? null : { queue: [...iids], done: 0, inFlight: null };
}

/**
 * The next sale to send, or `null` while one is out — THE ONE INVARIANT: a
 * caller that only sends what this returns never has two in flight.
 */
export function nextSale(batch: SellBatch): string | null {
  if (batch.inFlight !== null) return null;
  return batch.queue[batch.done] ?? null;
}

/** Records that `iid` has gone out and is waiting for its verdict. */
export function markSent(batch: SellBatch, iid: string): SellBatch {
  return { queue: batch.queue, done: batch.done, inFlight: iid };
}

/**
 * One answer landed; `null` when that was the last. The verdict is not an
 * input: a refused sale has still been answered, and the profile after every
 * answer redraws what happened.
 */
export function saleAnswered(batch: SellBatch): SellBatch | null {
  const done = batch.done + 1;
  return done >= batch.queue.length ? null : { queue: batch.queue, done, inFlight: null };
}

/** What the button says while the batch runs: "Selling 3 of 8…". */
export function batchLabel(batch: SellBatch): string {
  const at = batch.done + 1 > batch.queue.length ? batch.queue.length : batch.done + 1;
  return `Selling ${at} of ${batch.queue.length}…`;
}
